import DOMPurify from 'dompurify';
import { FootNoteProps } from '../models/TopLevelTemplateComponentProps';
import { processFootnoteReferences } from './TemplateUtils';

export { sanitizeEmailHTML } from './sanitizeEmailHTML';

const config = {
  ALLOWED_TAGS: [
    'b',
    'i',
    'em',
    'strong',
    'u',
    'br',
    'p',
    'ul',
    'ol',
    'li',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'img',
    'sub',
    'sup',
  ],
  ALLOWED_ATTR: ['src', 'alt', 'loading', 'decoding'],
};

const IMG_PLACEHOLDER_REGEX = /#img:([^:#]+):([^#]+)#/g;

const escapeAttr = (value: string) =>
  value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

export const convertImgPlaceholders = (html: string): string => {
  return html.replace(IMG_PLACEHOLDER_REGEX, (_m, rawPath: string, rawAlt: string) => {
    const path = String(rawPath || '')
      .trim()
      .replace(/^\/+/, '');
    const alt = String(rawAlt || '').trim();

    if (!path || path.includes('..')) {
      return '';
    }

    const src = path.startsWith('api/images/') ? '/' + path : '/assets/' + path;
    const safeSrc = escapeAttr(src);
    const safeAlt = escapeAttr(alt);
    return `<img src="${safeSrc}" alt="${safeAlt}" loading="lazy" decoding="async" />`;
  });
};

const stripDisallowedImages = (html: string): string => {
  try {
    if (typeof document === 'undefined') return html;

    const wrapper = document.createElement('div');
    wrapper.innerHTML = html;

    const images = Array.from(wrapper.getElementsByTagName('img'));
    for (const img of images) {
      const srcAttr = img.getAttribute('src') || '';
      const allowed =
        (srcAttr.startsWith('/assets/') || srcAttr.startsWith('/api/images/')) &&
        !srcAttr.includes('..');
      if (!allowed) {
        img.remove();
      }
    }
    return wrapper.innerHTML;
  } catch (_e) {
    return html;
  }
};

const sanitizeWithConfig = (
  html: string,
  domPurifyConfig: typeof config,
  options?: { stripSurrounding?: string; footnotes?: FootNoteProps[] | null },
): string => {
  const expanded = convertImgPlaceholders(html);
  let newHtml = DOMPurify.sanitize(expanded, domPurifyConfig) as unknown as string;
  newHtml = stripDisallowedImages(newHtml);

  if (options?.footnotes) {
    newHtml = processFootnoteReferences(newHtml, options.footnotes);
  }

  if (options?.stripSurrounding) {
    return newHtml.replace(
      new RegExp(`^<${options.stripSurrounding}>|</${options.stripSurrounding}>$`, 'g'),
      '',
    );
  }

  return newHtml;
};

const sanitizeHTML = (
  html: string,
  options?: { stripSurrounding?: string; footnotes?: FootNoteProps[] | null },
): string => sanitizeWithConfig(html, config, options);

const diffConfig = {
  ...config,
  ALLOWED_TAGS: [...config.ALLOWED_TAGS, 'div', 'span'],
  ALLOWED_ATTR: [...config.ALLOWED_ATTR, 'data-diff-key', 'data-diff-mode', 'class'],
};

export const sanitizeHTMLWithDiff = (
  html: string,
  options?: { stripSurrounding?: string; footnotes?: FootNoteProps[] | null },
): string => sanitizeWithConfig(html, diffConfig, options);

export default sanitizeHTML;
